import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../api'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../components/Toasts'
import { ErrorState, EmptyLibrary, LoadingRows, NoResults } from '../components/States'
import { StatusPill } from '../components/StatusPill'
import { PageNumbers } from '../components/PageNumbers'
import { useAsync, useDebounced } from '../lib/useAsync'
import { count, rangeShort, today } from '../lib/format'
import { saveCsv } from '../lib/download'
import { ApprovalsTab } from './Approvals'

const COLUMNS = ['Title', 'Job family', 'Level', 'Location', 'Range', 'Ver.', 'Status', 'Updated']

/**
 * Twenty roles a page, the same as Benchmarking and Salary ranges, so every
 * list in the product pages alike (FR-2b).
 */
const PAGE_SIZE = 20

export function Browse() {
  const { me, isAdmin, noteRoleCount } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const tab = params.get('tab') === 'approvals' ? 'approvals' : 'all'
  const query = params.get('q') ?? ''
  const debounced = useDebounced(query)
  const [exporting, setExporting] = useState(false)
  const [preparing, setPreparing] = useState<{ done: number; of: number } | null>(null)

  // Which page, as a number, because a numbered pager jumps. The server says
  // how many pages there are, so every number offered is a page that exists.
  const [at, setAt] = useState(0)

  const list = useAsync(
    () => api.listJobDescriptions({ q: debounced, page: at, limit: PAGE_SIZE }),
    [debounced, at],
  )

  const setQuery = (q: string) => {
    const next = new URLSearchParams(params)
    if (q) next.set('q', q)
    else next.delete('q')
    setParams(next, { replace: true })
    // A new search is a new result set, so any position in the old one is
    // meaningless. Without this, searching from page 3 shows page 3 of the
    // new results and looks empty.
    setAt(0)
  }

  const setTab = (t: 'all' | 'approvals') => {
    const next = new URLSearchParams(params)
    if (t === 'approvals') next.set('tab', 'approvals')
    else next.delete('tab')
    setParams(next)
  }

  /*
   * Every role in the list or the search, as one PDF, a role to a page.
   *
   * The list on screen is one page of twenty and holds what the index holds,
   * so the whole list is read by marker and each role is then fetched whole.
   * That takes a moment for a big library, so the button says how far it has
   * got rather than sitting there looking pressed.
   */
  const downloadPdf = async () => {
    setExporting(true)
    try {
      const [{ collectRoles, libraryBlocks, libraryFileName }, { renderPdf }] = await Promise.all([
        import('../lib/role-pdf'), import('../lib/pdf'),
      ])
      const roles = await collectRoles(api, debounced, (done, of) => setPreparing({ done, of }))
      const fileName = libraryFileName(debounced, today())
      await renderPdf(libraryBlocks(roles), fileName)
      toast.ok(`Downloaded ${fileName}`)
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'The download did not start.')
    } finally {
      setExporting(false)
      setPreparing(null)
    }
  }

  /** The same roles as the spreadsheet mass upload reads back in (FR-12). */
  const downloadCsv = async () => {
    setExporting(true)
    try {
      const { fileName, body } = await api.exportCsv(debounced)
      saveCsv(fileName, body)
      toast.ok(`Downloaded ${fileName}`)
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'The download did not start.')
    } finally {
      setExporting(false)
    }
  }

  const matched = list.data?.matched ?? 0
  const total = list.data?.total ?? 0

  // The sidebar shows this same number, so it takes it from here rather than
  // from the session, and the two cannot show different things on one screen.
  useEffect(() => {
    if (list.data) noteRoleCount(list.data.total)
  }, [list.data, noteRoleCount])
  const pending = me?.pendingApprovals ?? 0

  const shown = list.data?.items.length ?? 0
  // The page the server served, which is the last one when the one asked for
  // has gone past the end.
  const here = list.data?.page ?? 0
  const pages = list.data?.pages ?? 1
  const from = here * PAGE_SIZE + 1
  const to = from + shown - 1
  const paged = pages > 1

  const subline = list.loading
    ? 'Loading the library…'
    : query.trim()
      ? `${matched} of ${total} job descriptions match`
      : `${total} job description${total === 1 ? '' : 's'}`

  // The download button says how many roles it will contain (FR-4).
  const downloadLabel = query.trim() ? `Download these ${count(matched)}` : `Download all ${count(total)}`

  return (
    <div className="screen">
      <div className="sticky-head">
        <div className="title-row">
          <div>
            <h1 className="page">Job descriptions</h1>
            <p className="subline">{subline}</p>
          </div>
          <div className="actions">
            <button
              type="button"
              className="btn"
              onClick={() => void downloadPdf()}
              disabled={exporting || list.loading || matched === 0}
            >
              {preparing ? `Preparing ${count(preparing.done)} of ${count(preparing.of)}…` : `↓ ${downloadLabel}`}
            </button>
            <button
              type="button"
              className="btn btn--sm btn--quiet"
              onClick={() => void downloadCsv()}
              disabled={exporting || list.loading || matched === 0}
              title="The same roles as a spreadsheet, in the format mass upload reads"
            >
              Spreadsheet
            </button>
            {isAdmin && (
              <>
                <button type="button" className="btn btn--indigo" onClick={() => navigate('/job-descriptions/bulk')}>
                  Mass upload
                </button>
                <button type="button" className="btn btn--primary" onClick={() => navigate('/job-descriptions/new')}>
                  + New job description
                </button>
              </>
            )}
          </div>
        </div>

        {tab === 'all' && (
          <div className="search-row">
            <label className="sr-only" htmlFor="jd-search">Search job descriptions</label>
            <input
              id="jd-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title, family, level, location or skill…"
            />
            {query && (
              <button type="button" className="btn btn--sm" onClick={() => setQuery('')}>
                Clear
              </button>
            )}
            <span className="count" aria-live="polite">
              {list.loading
                ? 'Searching…'
                : paged
                  ? `Showing ${count(from)} to ${count(to)} of ${count(matched)}`
                  : `${count(matched)} of ${count(total)} match`}
            </span>
          </div>
        )}

        <div className="tabs" role="tablist" aria-label="Library views">
          <button
            type="button"
            role="tab"
            className="tab"
            aria-selected={tab === 'all'}
            onClick={() => setTab('all')}
          >
            All job descriptions
          </button>
          <button
            type="button"
            role="tab"
            className="tab"
            aria-selected={tab === 'approvals'}
            onClick={() => setTab('approvals')}
          >
            Approvals
            {pending > 0 && (
              <span className="badge">
                {pending}
                <span className="sr-only"> waiting on you</span>
              </span>
            )}
          </button>
        </div>
      </div>

      {tab === 'approvals' ? (
        <ApprovalsTab />
      ) : list.error ? (
        <ErrorState message={list.error} onRetry={list.reload} />
      ) : list.loading ? (
        <div className="card card--flush">
          <LoadingRows rows={7} columns={6} />
        </div>
      ) : total === 0 ? (
        <EmptyLibrary
          canCreate={isAdmin}
          onCreate={() => navigate('/job-descriptions/new')}
          onBulk={() => navigate('/job-descriptions/bulk')}
        />
      ) : matched === 0 ? (
        <div className="card">
          <NoResults query={query} onClear={() => setQuery('')} />
        </div>
      ) : (
        <div className="card card--flush">
          <div className="table-wrap">
            <table>
              <caption className="sr-only">
                {matched} job description{matched === 1 ? '' : 's'}, most recently changed first
              </caption>
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c} scope="col">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.data?.items.map((j) => (
                  <tr
                    key={j.id}
                    className="row"
                    onClick={() => navigate(`/job-descriptions/${j.id}`)}
                  >
                    {/* The title is the real link, so the keyboard gets one
                        stop per row and a screen reader gets a named target.
                        Clicking anywhere on the row is the mouse shortcut. */}
                    <td className="strong nowrap">
                      <Link to={`/job-descriptions/${j.id}`} className="row__link">
                        {j.title}
                      </Link>
                    </td>
                    <td className="nowrap">{j.family}</td>
                    <td className="nowrap">{j.level}</td>
                    <td className="nowrap">{j.location}</td>
                    <td className="nowrap num">{rangeShort(j.payMin, j.payMax, j.payUnit)}</td>
                    <td className="dim">v{j.version}</td>
                    <td><StatusPill status={j.status} /></td>
                    <td className="dim nowrap">{j.updated}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Only when there is more than one page, as on every other tab. */}
          {paged && (
            <nav className="pager" aria-label="Library pages">
              <p className="subline">
                Page {count(here + 1)} of {count(pages)} · showing {count(from)} to {count(to)} of{' '}
                {count(matched)}{query.trim() ? ' matching' : ''}
              </p>
              <PageNumbers
                at={here}
                pages={pages}
                open={false}
                busy={list.loading}
                onGo={(n) => setAt(Math.max(0, Math.min(n, pages - 1)))}
              />
            </nav>
          )}
        </div>
      )}
    </div>
  )
}
